import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { scrollTo } from "@/components/Nav";
import { useCart } from "@/context/CartContext";

const EASE = [0.16, 1, 0.3, 1];

const ACTIVITY = [
  { id: "sedentary", label: "Sedentary", factors: { maintain: 0.8, build: 1.4, cut: 1.2 } },
  { id: "active", label: "Active", factors: { maintain: 1.2, build: 1.8, cut: 1.6 } },
  { id: "athlete", label: "Athlete", factors: { maintain: 1.6, build: 2.2, cut: 2.0 } },
];

const GOALS = [
  { id: "maintain", label: "Maintain" },
  { id: "build", label: "Build Muscle" },
  { id: "cut", label: "Fat Loss" },
];

const bmiCategory = (bmi) =>
  bmi < 18.5 ? "Underweight" : bmi < 25 ? "Optimal" : bmi < 30 ? "Overweight" : "Obese";

const PillGroup = ({ options, value, onChange, tid }) => (
  <div className="flex flex-wrap gap-3">
    {options.map((o) => (
      <button
        key={o.id}
        type="button"
        onClick={() => onChange(o.id)}
        data-testid={`${tid}-${o.id}`}
        className={`rounded-full px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors duration-500 ${
          value === o.id
            ? "bg-[#111111] text-[#F9F8F6] border-[#111111]"
            : "border-[#EBE8E3] text-[#5C5C5C] hover:border-[#C07C5F] hover:text-[#111111]"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const BMICalculator = () => {
  const { setSuggestion } = useCart();
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [activity, setActivity] = useState("active");
  const [goal, setGoal] = useState("build");
  const [result, setResult] = useState(null);

  const calculate = (e) => {
    e.preventDefault();
    const h = parseFloat(height);
    const w = parseFloat(weight);
    if (!h || !w || h < 100 || h > 250 || w < 25 || w > 300) return;
    const bmi = w / Math.pow(h / 100, 2);
    const factor = ACTIVITY.find((a) => a.id === activity).factors[goal];
    const protein = Math.round(w * factor);
    const size = protein >= 130 ? 9 : 6;
    const collagen = goal === "build" ? Math.round(size / 3) : goal === "cut" ? Math.round((2 * size) / 3) : Math.round(size / 2);
    setSuggestion({ size, collagen });
    setResult({
      bmi: bmi.toFixed(1),
      category: bmiCategory(bmi),
      protein,
      barShare: Math.min(100, Math.round((20 / protein) * 100)),
      box: { size, collagen },
    });
  };

  return (
    <section id="calculator" className="py-32 md:py-48 border-b border-[#EBE8E3]" data-testid="calculator-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
          className="grid md:grid-cols-12 gap-8 mb-20 md:mb-28"
        >
          <p className="md:col-span-3 text-sm md:text-base tracking-[0.3em] uppercase text-[#C07C5F]">
            The Measure
          </p>
          <h2 className="md:col-span-9 font-serif-lux font-medium text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            Calculate Your
            <span className="italic font-light text-[#5C5C5C]"> Protein Index</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-16 md:gap-24">
          <motion.form
            onSubmit={calculate}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="md:col-span-6 flex flex-col gap-12"
            data-testid="bmi-form"
          >
            <div className="grid grid-cols-2 gap-10">
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Height — cm</span>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  data-testid="bmi-height-input"
                  placeholder="172"
                  className="w-full bg-transparent border-b border-[#111111] py-3 mt-1 outline-none focus:border-[#C07C5F] transition-colors duration-500 font-serif-lux text-3xl font-light"
                />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Weight — kg</span>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  data-testid="bmi-weight-input"
                  placeholder="68"
                  className="w-full bg-transparent border-b border-[#111111] py-3 mt-1 outline-none focus:border-[#C07C5F] transition-colors duration-500 font-serif-lux text-3xl font-light"
                />
              </label>
            </div>

            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-4">Lifestyle</p>
              <PillGroup options={ACTIVITY} value={activity} onChange={setActivity} tid="bmi-activity" />
            </div>

            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-4">Goal</p>
              <PillGroup options={GOALS} value={goal} onChange={setGoal} tid="bmi-goal" />
            </div>

            <button
              type="submit"
              data-testid="bmi-calculate-btn"
              className="group self-start flex items-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500"
            >
              Calculate
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </button>
          </motion.form>

          <div className="md:col-span-6 flex items-center">
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.8, ease: EASE }}
                  className="w-full border border-[#EBE8E3] bg-white p-10 md:p-14"
                  data-testid="bmi-result"
                >
                  <div className="flex items-end justify-between border-b border-[#EBE8E3] pb-8 mb-8">
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-2">Your BMI</p>
                      <p className="font-serif-lux font-medium text-7xl leading-none" data-testid="bmi-value">
                        {result.bmi}
                      </p>
                    </div>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#C07C5F] border border-[#C07C5F]/40 rounded-full px-4 py-2" data-testid="bmi-category">
                      {result.category}
                    </span>
                  </div>

                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-2">
                    Daily protein target
                  </p>
                  <p className="font-serif-lux font-medium text-6xl leading-none mb-6" data-testid="protein-target-value">
                    {result.protein}
                    <span className="italic font-light text-3xl text-[#5C5C5C]"> g / day</span>
                  </p>

                  <p className="text-sm font-light text-[#5C5C5C] leading-relaxed mb-8" data-testid="protein-bar-share">
                    Two DNA bars give you 20g — {result.barShare}% of your daily target,
                    wrapped in collagen or creatine couture.
                  </p>

                  <div className="border border-[#EBE8E3] px-5 py-4 mb-10 flex items-center justify-between gap-4" data-testid="bmi-smart-suggestion">
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#C07C5F] mb-1.5">
                        Smart Box Suggestion
                      </p>
                      <p className="font-serif-lux text-xl leading-tight" data-testid="bmi-smart-suggestion-text">
                        Box of {result.box.size} — {result.box.collagen} Collagen · {result.box.size - result.box.collagen} Creatine
                      </p>
                    </div>
                    <Sparkles size={16} className="text-[#C07C5F] shrink-0" />
                  </div>

                  <button
                    onClick={() => scrollTo("#build-box")}
                    data-testid="bmi-build-box-btn"
                    className="group flex items-center gap-3 border border-[#111111] rounded-full px-7 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#111111] hover:text-[#F9F8F6] transition-colors duration-500"
                  >
                    Build Your Box
                    <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full border border-dashed border-[#EBE8E3] p-10 md:p-14 text-center"
                  data-testid="bmi-placeholder"
                >
                  <p className="font-serif-lux italic font-light text-3xl md:text-4xl text-[#5C5C5C] leading-snug">
                    "To nourish beautifully,
                    <br />
                    first measure honestly."
                  </p>
                  <p className="text-[10px] tracking-[0.35em] uppercase text-[#C07C5F] mt-6">
                    Your results appear here
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BMICalculator;
